import { useQuery } from "@apollo/client/react";
import { DSCircularButton } from "@components/design-system/molecules";
import {
  faPhone,
  faPhoneSlash,
  faSpinner,
  faBackspace,
  faCog,
  faMicrophone,
  faMicrophoneSlash,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useState } from "react";

import { GET_USER_VERIFIED_PHONES } from "../../../../graphql/queries/PhoneVerification";
import PhoneNumberInput from "../../../common/PhoneNumberInput";
import { DIALPAD_NUMBERS } from "../constants";
import {
  DialpadButton,
  CallButton,
  BaseScribephoneButton,
} from "../shared/ScribephoneButtons";

const DialStep = ({
  setCurrentStep,
  phoneNumber,
  handleNumberPress,
  handleInputChange,
  handleEnterPress,
  handleBackspace,
  handleCallClient,
  isDialing,
  onOpenSettings,
  renderCallerIdButton,
  hasPhoneVerificationAccess = true,
  isConnected = false,
  getCurrentCall = null,
  onEndCall = null,
  toggleMute = null,
  isMuted = false,
  onDTMFSent = null,
  callStatus = "",
}) => {
  const [activeDigit, setActiveDigit] = useState(null);

  const { data: userPhonesData, loading: userPhonesLoading } =
    useQuery(GET_USER_VERIFIED_PHONES);

  const userVerifiedPhones =
    userPhonesData?.verifiedPhoneNumbers || [];

  const hasVerifiedPhones = userVerifiedPhones.length > 0;

  const getDisclaimerText = () => {
    if (isConnected) {
      return "Use the keypad to navigate phone menus.";
    }
    return "Your selected template and title will be used for the note generated from the call.";
  };

  const handleDigitPress = (digit) => {
    if (isConnected && getCurrentCall) {
      const call = getCurrentCall();
      if (call) {
        try {
          call.sendDigits(digit);
          setActiveDigit(digit);
          setTimeout(() => setActiveDigit(null), 200);
          if (onDTMFSent) {
            onDTMFSent(digit);
          }
        } catch (error) {
          console.error("Failed to send DTMF digit:", error);
        }
      }
    } else {
      handleNumberPress(digit);
    }
  };

  const renderConnectedHeader = () => {
    // Determine if we're actually connected or still ringing
    const isActuallyConnected = callStatus
      ?.toLowerCase()
      .includes("connected");
    const headerText = isActuallyConnected
      ? "Connected"
      : "Calling...";
    const dotColor = isActuallyConnected
      ? "bg-green-500"
      : "bg-yellow-500";

    return (
      <div className="text-center mb-4">
        <div className="flex items-center justify-center mb-2">
          <div
            className={`w-3 h-3 ${dotColor} rounded-full animate-pulse mr-2`}
          />
          <h3 className="text-lg font-semibold text-primary">
            {headerText}
          </h3>
        </div>
      </div>
    );
  };

  const renderPhoneInput = () => (
    <div className="mb-4">
      <div className="flex justify-between items-center mb-2">
        <label className="text-sm font-medium text-primary">
          Client Phone Number
        </label>
        {renderCallerIdButton ? (
          renderCallerIdButton()
        ) : hasPhoneVerificationAccess ? (
          <button
            onClick={onOpenSettings}
            className="flex items-center space-x-1 text-sm text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 transition-colors"
          >
            {userPhonesLoading ? (
              <FontAwesomeIcon
                icon={faSpinner}
                className="animate-spin"
              />
            ) : hasVerifiedPhones ? (
              <>
                <FontAwesomeIcon icon={faCog} />
                <span>Manage Numbers</span>
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={faCog} />
                <span>Add Number</span>
              </>
            )}
          </button>
        ) : null}
      </div>
      <PhoneNumberInput
        value={phoneNumber}
        onChange={handleInputChange}
        onEnterPress={handleEnterPress}
        className="text-black"
      />
    </div>
  );

  const renderDialControls = () => (
    <div className="grid grid-cols-3 gap-4 max-w-96 mx-auto">
      <div className="place-self-center">
        <DSCircularButton
          onClick={() => {
            setCurrentStep("settings");
          }}
          icon={faCog}
          dataTestId="scribephone-settings-button"
        />
      </div>
      <CallButton
        onClick={() => handleCallClient(false)}
        disabled={!phoneNumber.trim() || isDialing}
        icon={isDialing ? faSpinner : faPhone}
        className={isDialing ? "animate-spin" : ""}
      />
      {phoneNumber ? (
        <div className="place-self-center">
          <DSCircularButton
            onClick={handleBackspace}
            icon={faBackspace}
            dataTestId="scribephone-backspace-button"
          />
        </div>
      ) : (
        <div></div>
      )}
    </div>
  );

  const renderInCallControls = () => (
    <div className="grid grid-cols-3 gap-4 max-w-96 mx-auto">
      <BaseScribephoneButton
        onClick={toggleMute}
        variant={isMuted ? "danger" : "light"}
        size="sm"
        icon={isMuted ? faMicrophoneSlash : faMicrophone}
        className="place-self-center"
        dataTestId="scribephone-mute-button"
      />
      <BaseScribephoneButton
        onClick={onEndCall}
        disabled={isDialing}
        variant="danger"
        size="sm"
        icon={isDialing ? faSpinner : faPhoneSlash}
        className={isDialing ? "animate-spin" : ""}
        dataTestId="scribephone-end-call-button"
      />
      <div></div>
    </div>
  );

  return (
    <>
      {isConnected ? renderConnectedHeader() : renderPhoneInput()}

      <div className="grid grid-cols-3 gap-4 mb-6 max-w-96 mx-auto">
        {DIALPAD_NUMBERS.map((row) =>
          row.map(({ number, letters }) => (
            <DialpadButton
              key={number}
              number={number}
              letters={letters}
              onClick={() => handleDigitPress(number)}
              allowSpecialChars={isConnected}
              isActive={activeDigit === number}
            />
          )),
        )}
      </div>

      {isConnected ? renderInCallControls() : renderDialControls()}

      <p className="text-xs text-secondary text-center mt-4">
        {getDisclaimerText()}
      </p>
    </>
  );
};

export default DialStep;
